import { Cpu, HardDrive, MemoryStick, ShieldCheck, MessageCircle, BadgeCheck } from "lucide-react";
import { Reveal, SectionHeading, waLink } from "./shared";

// Curated refurbished stock — edit models, specs, prices & photos here anytime.
const LAPTOPS = [
  {
    name: "MacBook Air 13\"",
    badge: "Apple",
    price: "S$650",
    condition: "Grade A · Like New",
    cpu: "Intel Core i5",
    ram: "8GB RAM",
    storage: "256GB SSD",
    img: "https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    status: "in",
  },
  {
    name: "MacBook Pro 13\"",
    badge: "Apple",
    price: "S$820",
    condition: "Grade A · Excellent",
    cpu: "Intel Core i7",
    ram: "16GB RAM",
    storage: "512GB SSD",
    img: "https://images.unsplash.com/photo-1629131726692-1accd0c53ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    status: "in",
  },
  {
    name: "Dell Latitude 7490",
    badge: "Business",
    price: "S$420",
    condition: "Grade A · Great",
    cpu: "Intel Core i5",
    ram: "8GB RAM",
    storage: "256GB SSD",
    img: "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    status: "in",
  },
  {
    name: "HP ProBook 450",
    badge: "Business",
    price: "S$380",
    condition: "Grade B · Very Good",
    cpu: "Intel Core i5",
    ram: "8GB RAM",
    storage: "512GB SSD",
    img: "https://images.unsplash.com/photo-1593642702821-c8da6771f0c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    status: "in",
  },
  {
    name: "Gaming Laptop · RTX",
    badge: "Gaming",
    price: "S$980",
    condition: "Grade A · Powerful",
    cpu: "Intel Core i7",
    ram: "16GB RAM",
    storage: "1TB SSD",
    img: "https://images.unsplash.com/photo-1610465299993-e6675c9f9efa?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    status: "in",
  },
  {
    name: "Lenovo ThinkPad",
    badge: "Business",
    price: "S$300",
    condition: "Grade B · Reliable",
    cpu: "Intel Core i5",
    ram: "8GB RAM",
    storage: "256GB SSD",
    img: "https://images.pexels.com/photos/1466609/pexels-photo-1466609.jpeg?auto=compress&cs=tinysrgb&w=900",
    status: "last",
  },
];

export const RefurbishedStock = () => (
  <section id="refurbished" data-testid="refurbished-section" className="relative py-24 sm:py-32 bg-void overflow-hidden">
    <div className="absolute -bottom-24 right-1/4 w-[28rem] h-[28rem] rounded-full bg-brand-cyan/8 blur-[150px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Refurbished Laptops · In Stock"
        title="Certified laptops,"
        accent="smarter prices."
        sub="Every unit is tested, cleaned and certified by our workshop. Reserve one on WhatsApp — stock moves fast."
      />

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {LAPTOPS.map((l, i) => (
          <Reveal key={l.name} delay={i * 0.07}>
            <div
              data-testid={`laptop-card-${i}`}
              className="group h-full bg-card/80 border border-slate-800 rounded-2xl overflow-hidden hover:border-brand-lime/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(170,255,0,0.12)] flex flex-col"
            >
              <div className="relative h-48 overflow-hidden bg-navy">
                <img
                  src={l.img}
                  alt={l.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/10 to-transparent" />
                <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.15em] text-void bg-brand-lime px-3 py-1 rounded-full font-bold">
                  {l.badge}
                </span>
                {l.status === "last" && (
                  <span className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.15em] text-white bg-red-500/90 px-3 py-1 rounded-full font-bold animate-pulse">
                    Last unit
                  </span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-xl font-bold uppercase text-white leading-tight">{l.name}</h3>
                  <span className="font-display text-2xl font-black text-brand-lime whitespace-nowrap">{l.price}</span>
                </div>
                <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-brand-cyan">
                  <BadgeCheck className="w-3.5 h-3.5" /> {l.condition}
                </p>
                <div className="mt-5 space-y-2.5">
                  <Spec icon={Cpu} text={l.cpu} />
                  <Spec icon={MemoryStick} text={l.ram} />
                  <Spec icon={HardDrive} text={l.storage} />
                </div>
                <a
                  data-testid={`laptop-reserve-${i}`}
                  href={waLink(`Hi AFZ Tech Solution, I'd like to reserve the refurbished ${l.name} (${l.price}). Is it still available?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 mt-auto inline-flex items-center justify-center gap-2 bg-brand-lime text-void font-display font-bold uppercase text-xs sm:text-sm tracking-wide px-4 py-2.5 sm:px-5 sm:py-3 rounded-full hover:bg-brand-limedark hover:scale-[1.02] transition-all duration-300 shadow-[0_0_22px_rgba(170,255,0,0.35)]"
                >
                  <MessageCircle className="w-4 h-4 shrink-0" /> Reserve on WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <p className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400">
          <ShieldCheck className="w-4 h-4 text-brand-lime" />
          Every refurbished laptop is tested, cleaned and backed by warranty. Looking for something specific?{" "}
          <a
            data-testid="refurbished-request-link"
            href={waLink("Hi AFZ Tech Solution, I'm looking for a specific refurbished laptop. Can you help?")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-lime hover:underline font-semibold"
          >
            Ask us
          </a>
          .
        </p>
      </Reveal>
    </div>
  </section>
);

const Spec = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2.5">
    <Icon className="w-4 h-4 text-brand-cyan shrink-0" />
    <span className="text-sm text-slate-300">{text}</span>
  </div>
);
